import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import ThriveModal from "../../../common/ThriveModal";
import ThriveCaseSelection from "../../../common/ThriveCaseSelection";
import { handleActiveClients } from "../legacyExports";
import ProgramStatsSection from "../programStats/ProgramStatsSection";
import { ConsultantOption, TabProps } from "../types";

const PRIMARY = "#9D2362";
const CARD_BORDER = "1px solid #ececf0";

// Server bridge into Metabase, on the same API base as axiosInstance.
const METABASE_OPEN_URL = `${process.env.REACT_APP_API_DOMAIN}/metabase/open`;

interface DataTabProps extends TabProps {
  isAdmin: boolean;
  consultants: ConsultantOption[];
  currentUser: any;
}

/**
 * Two things: the remaining legacy Reports page (the Metabase entry point, raw
 * downloads and the older report links), and below it Program Statistics
 * (`programStats/`), which follows the hub's date range and consultant filter.
 *
 * Metabase was hidden entirely on 2026-08-30 (Report Dashboard / Learn buttons
 * and the question list; the server's `GET /reports/metabase/questions` proxy
 * is gone as well — the old question inventory is in
 * thrive_server/claude_doc/metabase_migration/inventory.md). One entry point
 * came back on 2026-10-08: "Report Dashboard" links to the server bridge
 * `GET /metabase/open`, which reads the Thrive login cookie, signs the user
 * into Metabase and redirects there (or to the Thrive login page when the
 * user is not logged in).
 *
 * It has to stay a plain link opened by the click itself: the bridge answers
 * with a redirect and a Metabase cookie, which only a top-level navigation can
 * use, and a window opened after an awaited request is stopped by popup
 * blockers. Never fetch it with axios.
 */
const DataTab: React.FC<DataTabProps> = ({
  start,
  end,
  consultant,
  onDrill,
  isAdmin,
  consultants,
  currentUser,
}) => {
  const navigate = useNavigate();
  const [allCaseCardOpen, setAllCaseCardOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState<any>({
    _id: null,
    firmRecord: {},
  });

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{ p: 3, borderRadius: "16px", border: CARD_BORDER }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: "#2c2c34", mb: 0.5 }}
        >
          Data &amp; raw exports
        </Typography>
        <Typography variant="body2" sx={{ color: "#8a8a93", mb: 2 }}>
          Legacy report links and CSV downloads. These are not affected by the
          date range above.
        </Typography>

        <Table sx={{ maxWidth: 900 }}>
          <TableBody>
            <TableRow>
              <TableCell colSpan={2}>
                <Button
                  href={METABASE_OPEN_URL}
                  target="_blank"
                  rel="noopener"
                  endIcon={<OpenInNewIcon />}
                  sx={{ color: PRIMARY, fontWeight: 600 }}
                >
                  Report Dashboard
                </Button>
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "#8a8a93", px: 1 }}
                >
                  Opens the report builder in a new tab and signs you in
                  automatically.
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Button
                  onClick={() => setAllCaseCardOpen(true)}
                  sx={{ color: PRIMARY, fontWeight: 600 }}
                >
                  Active Clients Report
                </Button>
              </TableCell>
              <TableCell align="right">
                <Button onClick={handleActiveClients}>Download Raw</Button>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Button
                  onClick={() => navigate("/dashboard/reports/initial_contact")}
                  sx={{ color: PRIMARY, fontWeight: 600 }}
                >
                  Initial Contacts Report
                </Button>
              </TableCell>
              <TableCell align="right" />
            </TableRow>
            {/* RGA link hidden 2026-08-30 (grant program concluded);
                /dashboard/reports/rga stays reachable by URL. */}
          </TableBody>
        </Table>
      </Paper>

      <ProgramStatsSection
        start={start}
        end={end}
        consultant={consultant}
        onDrill={onDrill}
        isAdmin={isAdmin}
        consultants={consultants}
        currentUser={currentUser}
      />

      <ThriveModal
        height={700}
        open={allCaseCardOpen}
        contentComponent={
          <ThriveCaseSelection
            height={500}
            cases={[]}
            reloadCases={true}
            checkedCases={[]}
            setCheckedCases={() => {}}
            selectedCase={selectedCase}
            setSelectedCase={setSelectedCase}
            enableControl={true}
            enableSelection={true}
            enableMultiSelection={false}
            setOpen={setAllCaseCardOpen}
            title={"All Cases"}
            status_default="either"
            sortby_default="recent"
            period_default="all"
            handleNext={() => {
              if (selectedCase._id) {
                window.open(`/firmReportPdf?id=${selectedCase._id}`, "_blank");
              } else {
                console.error("No case selected");
              }
            }}
          />
        }
      />
    </Box>
  );
};

export default DataTab;
